/**
 * Whether the viewer's top-right analysis column has anything to show.
 *
 * Extracted from `Viewer.tsx` because the inline `a.length > 0 || b.length > 0
 * || …` expression is a **silent single point of failure for every analysis
 * feature**: the column is the only mount point for the analysis chips, and a
 * new analysis that forgets to extend the expression ships with no entry point
 * at all while every one of its own tests passes. That is exactly what happened
 * to the clearance chip (Task 15, review H1) — the chip, the dock and eleven
 * dock tests were all correct, and the feature was still unreachable on a
 * project whose only analysis output was a clearance run.
 *
 * Keeping it here means the invariant has a test of its own, and the compiler
 * makes the omission impossible: `AnalysisColumnCounts` is an exact record, so
 * adding a member without counting it does not type-check.
 */
export interface AnalysisColumnCounts {
  /** Rainfall summary panels (vector layers with a rainfall run). */
  rainfall: number;
  /** Deviation colorbars / volume panels. */
  deviation: number;
  /** Surface-difference panels (the visible GLB layers). */
  surfaceDifference: number;
  /** Clearance chips — derived clouds carrying `metadata.clearance`. */
  clearance: number;
}

export function analysisColumnHasContent(counts: AnalysisColumnCounts): boolean {
  return Object.values(counts).some((n) => n > 0);
}
